import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, EyeOff, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const tooShort = password.length > 0 && password.length < 6;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = useMemo(
    () => Boolean(token) && password.length >= 6 && password === confirm,
    [token, password, confirm]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in with your new password.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#09090B] text-white relative overflow-hidden">
        <Link to="/" className="inline-block bg-white rounded-xl p-3 shadow-sm self-start">
          <img
            src="/scanmint-logo.png"
            alt="ScanMint"
            className="h-12 w-auto block"
            draggable={false}
          />
        </Link>
        <div>
          <div className="mono-tiny text-white/50 mb-3">Choose a new password</div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Almost there. Pick a strong new password.
          </h2>
          <p className="mt-4 text-white/60 max-w-sm">
            Reset links can only be used once and stop working 30 minutes
            after they were sent.
          </p>
        </div>
        <div className="mono-tiny text-white/40">© 2026 ScanMint</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md space-y-6"
          data-testid="reset-password-form"
        >
          <div className="md:hidden mb-4">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Set a new password
            </h1>
            <p className="text-sm text-[#71717A] mt-2">
              Pick something at least 6 characters long. You'll be signed out
              everywhere after this.
            </p>
          </div>

          {!token && (
            <div
              className="flex items-start gap-3 border border-[#FACC15]/40 bg-[#FFFBEA] rounded-md p-4 text-sm text-[#92400E]"
              data-testid="reset-password-missing-token"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                This reset link is missing its token. Please request a new
                reset email.
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="mono-tiny text-[#71717A]">
                New password
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-12"
                  data-testid="reset-password-new"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#09090B] transition-colors p-1 rounded focus:outline-none focus:ring-2 focus:ring-[#0F8F4F]/40"
                  data-testid="reset-password-toggle"
                  tabIndex={-1}
                >
                  {showPwd ? (
                    <EyeOff className="w-4 h-4" strokeWidth={1.75} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={1.75} />
                  )}
                </button>
              </div>
              {tooShort && (
                <div className="text-xs text-[#92400E]" data-testid="reset-password-too-short">
                  Password must be at least 6 characters.
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="mono-tiny text-[#71717A]">
                Confirm new password
              </Label>
              <Input
                id="confirm-password"
                type={showPwd ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-12"
                data-testid="reset-password-confirm"
                required
                minLength={6}
              />
              {mismatch && (
                <div className="text-xs text-[#92400E]" data-testid="reset-password-mismatch">
                  Passwords don't match.
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !canSubmit}
            className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-base"
            data-testid="reset-password-submit"
          >
            {loading ? "Saving…" : "Save new password"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>

          <div className="text-sm">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-[#0F8F4F] font-medium hover:underline"
              data-testid="reset-password-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
